import { CircleStop, Eye } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useActiveJumperSession, useIntl, useStopJumperSession } from '../../hooks';
import { hzToMhz } from '../../lib/frequency.js';
import { formatNumber } from '../../lib/number-format.js';
import { StopSessionConfirm } from './StopSessionConfirm.js';

type JumperSessionAlertProps = {
  deviceId: string;
  onOpenDetails: () => void;
};

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function JumperSessionAlert({ deviceId, onOpenDetails }: JumperSessionAlertProps) {
  const intl = useIntl();
  const active = useActiveJumperSession(deviceId);
  const stopJumperSession = useStopJumperSession(deviceId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sessionId = active.session?.id ?? null;
  const handleStopConfirm = useCallback(async () => {
    if (!sessionId) return;
    await stopJumperSession.mutateAsync(sessionId);
    setConfirmOpen(false);
  }, [sessionId, stopJumperSession]);

  if (!active.session) return null;

  const { session, secondsRemaining } = active;
  const startMHz = formatNumber(hzToMhz(session.startFrequencyHz), { maximumFractionDigits: 5 });
  const endMHz = formatNumber(hzToMhz(session.endFrequencyHz), { maximumFractionDigits: 5 });
  const mmss = formatMmSs(secondsRemaining);
  const viewDetailsLabel = intl.formatMessage({ id: 'jumperSession.alert.viewDetails' });
  const stopLabel = intl.formatMessage({ id: 'jumperSession.alert.stop' });

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="mb-3 flex items-center justify-between gap-2 rounded-md border border-yellow bg-yellow-light px-3 py-2 text-xs text-yellow-dark shrink-0"
      >
        <span className="flex items-center gap-2">
          {intl.formatMessage({ id: 'jumperSession.alert.label' }, { startMHz, endMHz })}
          <span data-testid="alert-countdown" className="tabular-nums font-mono">
            {mmss}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            className="shrink-0 hover:opacity-70 focus:outline-none"
            onClick={onOpenDetails}
            title={viewDetailsLabel}
            aria-label={viewDetailsLabel}
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="shrink-0 hover:opacity-70 focus:outline-none disabled:opacity-50"
            onClick={() => setConfirmOpen(true)}
            disabled={stopJumperSession.isPending}
            title={stopLabel}
            aria-label={stopLabel}
          >
            <CircleStop className="h-4 w-4" aria-hidden="true" />
          </button>
        </span>
      </div>

      <StopSessionConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleStopConfirm}
        isPending={stopJumperSession.isPending}
      />
    </>
  );
}
